import { WarningOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { message, Popconfirm } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type UserSpecsFragment } from '../../../api/fragments/UserSpecs';
import {
    DeleteUserDocument,
    type DeleteUserMutation,
    type DeleteUserMutationVariables,
} from '../../../api/mutations/deleteUser';
import { useCompanyContext } from '../../../components/contexts/CompanyContextManager';
import useDeleteUserModal from './DeleteUserModal';

export type DeleteUserProps = {
    user: UserSpecsFragment;
    onClose: () => void;
    visible: boolean;
};

const DeleteUser = ({ user, onClose, visible }: DeleteUserProps) => {
    const { t } = useTranslation(['userDetails']);
    const navigate = useNavigate();
    const deleteUserModal = useDeleteUserModal();

    const apolloClient = useApolloClient();
    const userId = user.id;

    const companyContext = useCompanyContext();
    const displayCompany = useCallback(
        (companyId: string) => companyContext.availableCompanies.some(({ id }) => id === companyId),
        [companyContext]
    );

    const onDelete = useCallback(
        async (newAssigneeId?: string) => {
            message.loading({
                content: t('userDetails:deleteUser.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            const response = await apolloClient
                .mutate<DeleteUserMutation, DeleteUserMutationVariables>({
                    mutation: DeleteUserDocument,
                    variables: { id: userId, ...(newAssigneeId && { newAssigneeId }) },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            if (response?.data?.result?.success) {
                message.success(t('userDetails:deleteUser.messages.success'));

                navigate('/admin/accesses/users');
            } else {
                deleteUserModal.open(response?.data?.result);
            }

            onClose();
        },
        [t, apolloClient, userId, onClose, navigate, deleteUserModal]
    );

    return (
        <>
            <Popconfirm
                cancelText={t('userDetails:deleteUser.cancelText')}
                icon={<WarningOutlined style={{ color: 'red' }} />}
                okText={t('userDetails:deleteUser.okText')}
                onCancel={onClose}
                onConfirm={() => onDelete()}
                open={visible}
                placement="left"
                title={t('userDetails:deleteUser.message')}
            />
            {deleteUserModal.render({ userId, displayCompany, onRetry: onDelete })}
        </>
    );
};

export default DeleteUser;

export const useDeleteUserConfirmation = (user: DeleteUserProps['user']) => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: () => <DeleteUser onClose={actions.close} user={user} visible={visible} />,
    };
};
